import type { ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Tecla de un atajo dentro de un tooltip (fondo oscuro). */
export function TooltipKbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded bg-white/15 px-1 font-mono text-[10px] leading-none">
      {children}
    </kbd>
  );
}

/**
 * Tooltip (shadcn) para botones de acción, con el atajo de teclado como teclas.
 *
 * - `label`: texto. Si termina en "(…)" y no se pasa `shortcut`, ese paréntesis
 *   se toma como atajo: "Vista previa del PDF (⌘ Shift P)" → texto + teclas.
 * - El botón va envuelto en un <span>: un botón `disabled` no recibe eventos
 *   del mouse y el tooltip (que explica por qué está deshabilitado) no se vería.
 */
export function ActionTooltip({
  label,
  shortcut,
  side = "bottom",
  children,
}: {
  label: ReactNode;
  shortcut?: string;
  side?: "top" | "right" | "bottom" | "left";
  children: ReactNode;
}) {
  let text = label;
  let keys = shortcut;
  if (keys === undefined && typeof label === "string") {
    const m = /^(.*?)\s*\(([^()]+)\)$/.exec(label);
    if (m) {
      text = m[1];
      keys = m[2];
    }
  }
  const keyParts = keys?.split(/[\s +]+/).filter(Boolean) ?? [];

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent side={side} className="flex items-center gap-2">
        <span>{text}</span>
        {keyParts.length > 0 && (
          <span className="flex items-center gap-0.5">
            {keyParts.map((key, i) => (
              <TooltipKbd key={`${key}-${i}`}>{key}</TooltipKbd>
            ))}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
